/**
 * @Author: Zero
 * @Date: 2026-07-29 10:09:33
 * @Explain: 自定义滚动列表组件，支持分页和滚动加载更多（仅供pagingSelectGroup使用；暂不支持单选、且多选情况暂不支持父级勾选）
 */
import classNames from 'classnames';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Checkbox } from 'tdesign-react';
// components
import { Icon } from '@/components/tdesign';

interface TreeNode {
  id: string;
  value: string;
  text: string;
  label: string;
  content?: React.ReactNode;
  selectable?: boolean;
  children?: TreeNode[];
  groupKey?: string;
  isGroup?: boolean;
  expanded?: boolean;
}

interface CustomTreeProps {
  data: TreeNode[];
  expanded: string[];
  onExpand: (expandedIds: string[]) => void;
  onChange: (value: any, contextData: any) => void;
  defaultValue?: any;
  multiple?: boolean;
  onScroll?: (e: React.UIEvent<HTMLDivElement> & Event) => void;
  reqStatus?: string;
}

const CustomTree: React.FC<CustomTreeProps> = ({
  data = [],
  expanded,
  onExpand,
  onChange,
  defaultValue,
  multiple = true,
  onScroll,
  reqStatus,
}) => {
  const [expandedIds, setExpandedIds] = useState<string[]>(expanded);
  const [selectedIds, setSelectedIds] = useState<string[]>(defaultValue || []);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 监听expanded属性变化，更新expandedIds状态
  useEffect(() => {
    setExpandedIds(expanded);
  }, [expanded]);

  // 递归渲染树形节点
  const renderTreeNode = useCallback(
    (node: TreeNode, level: number = 0): React.ReactNode => {
      const hasChildren = node.children && node.children.length > 0;
      const isExpanded = expandedIds.includes(node.id);
      const isSelected = selectedIds.includes(node.id);

      // 处理整行的点击逻辑
      const handleRowClick = () => {
        // 如果点击的是折叠行
        if (hasChildren) {
          // 若是折叠项（父节点），控制展开/收起
          const newExpandedIds = isExpanded
            ? expandedIds.filter((id) => id !== node.id)
            : [...expandedIds, node.id];
          setExpandedIds(newExpandedIds);
          onExpand(newExpandedIds);
        } else {
          // 暂不支持单选模式
          if (!multiple) return;

          // 点击的是选择项（叶子节点）
          const newSelectedIds = isSelected
            ? selectedIds.filter((id) => id !== node.id)
            : [...selectedIds, node.id];
          setSelectedIds(newSelectedIds);
          onChange(newSelectedIds, { node });
        }
      };

      return (
        <Fragment key={node.id}>
          <div
            style={{
              paddingLeft: `${level * 20}px`,
              cursor: hasChildren ? 'pointer' : 'default',
            }}
            className={classNames('custom-tree-node', {
              'is-expend-item': hasChildren, // 展开项
              'is-select-item': !hasChildren, // 选择项
            })}
            onClick={handleRowClick}
          >
            {hasChildren && (
              <Icon.Font
                fontSize={20}
                name={
                  isExpanded ? 'triangle-down-arrow' : 'triangle-right-arrow'
                }
              />
            )}
            {!hasChildren && <span className="prefix-block" />}
            {multiple && <Checkbox checked={isSelected} />}
            <div className="ellipsis pl-4 pr-4 ml-4">{node.label}</div>
          </div>
          {isExpanded && hasChildren && (
            <div className="is-children">
              {node.children!.map((child) => renderTreeNode(child, level + 1))}
            </div>
          )}
        </Fragment>
      );
    },
    [expandedIds, selectedIds, onExpand, onChange, multiple],
  );

  // 处理滚动事件
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (onScroll) {
        onScroll(e);
      }

      const el = e.currentTarget;
      const { scrollHeight, scrollTop, clientHeight } = el;

      if (scrollHeight <= Math.ceil(clientHeight + scrollTop + 1)) {
        if (reqStatus === 'loading' && !isLoading) {
          setIsLoading(true);
          // 模拟加载更多数据
          setTimeout(() => {
            setIsLoading(false);
          }, 1000);
        }
      }
    },
    [onScroll, reqStatus, isLoading],
  );

  // 生成完整的树形结构
  const treeContent = useMemo(() => {
    return data?.map((node) => renderTreeNode(node));
  }, [data, renderTreeNode]);

  return (
    <div
      ref={containerRef}
      className="custom-tree-container"
      style={{ maxHeight: '320px' }}
      onScroll={handleScroll}
    >
      {treeContent}
      {/* {isLoading && (
        // <div style={{ textAlign: 'center', padding: '10px' }}>加载中...</div>
        <Loading
          size="small"
          loading
          text={`${intlGet('COMMON.LOADING')}...`}
        />
      )} */}
    </div>
  );
};

export default CustomTree;
